import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AvatarComponent } from '../../../components/avatar/avatar.component';
import { PersonOption, PersonSelectComponent } from '../../../components/person-select/person-select.component';
import {
  ReleaseAssessment,
  ReleaseDecisionValue,
  ReleaseDecisionVersion,
  ReleaseRecommendation,
  SignoffRole,
} from '../../../models/release-assessment.model';
import { ProjectMemberService } from '../../../services/project-member.service';
import { ProjectRoleService } from '../../../services/project-role.service';
import { ReleaseService } from '../../../services/release.service';
import { FIELD_LENGTHS } from '../../../shared/field-lengths';
import { reviewerOptionsFromMembers } from '../../../shared/project-member-options';

interface AuflageEntwurf {
  text: string;
  wer: string | null;
}

interface SignoffZeile {
  rolle: SignoffRole;
  name: string | null;
  bestaetigtAm: string | null;
  fehlt: boolean;
  benachrichtigt: boolean;
  bestaetigbar: boolean;
  auswahl: boolean;
  leerErlaubt: boolean;
  inhaberId: string | null;
}

const I18N = 'testResults.freigabe.bewertung.';
const I18N_ENTSCHEIDUNG = 'testResults.freigabe.entscheidung.';

const ROLLEN: readonly SignoffRole[] = ['TESTMANAGER', 'PRODUCT_OWNER', 'ENTWICKLUNGSLEITUNG'];

const DECISIONS: readonly ReleaseDecisionValue[] = ['GO', 'AUFLAGEN', 'NOGO'];

const DECISION_FOR_RECOMMENDATION: Record<ReleaseRecommendation, ReleaseDecisionValue> = {
  GO: 'GO',
  GO_AUFLAGEN: 'AUFLAGEN',
  NO_GO: 'NOGO',
};

const LEERE_AUFLAGE: AuflageEntwurf = { text: '', wer: null };

const SAVE_ERROR_BY_CODE: Record<string, string> = {
  DECISION_CONDITION_INVALID: `${I18N_ENTSCHEIDUNG}errors.conditionInvalid`,
  DECISION_ROLE_UNASSIGNED: `${I18N_ENTSCHEIDUNG}errors.roleUnassigned`,
};

const ASSIGN_ERROR_BY_CODE: Record<string, string> = {
  SIGNOFF_ASSIGNEE_INVALID: `${I18N_ENTSCHEIDUNG}errors.assigneeInvalid`,
  SIGNOFF_ASSIGNEE_DUPLICATE: `${I18N_ENTSCHEIDUNG}errors.assigneeDuplicate`,
  SIGNOFF_ROLE_REQUIRED: `${I18N_ENTSCHEIDUNG}errors.roleRequired`,
};

function errorCode(error: unknown): string | undefined {
  return error instanceof HttpErrorResponse ? error.error?.code : undefined;
}

function isForbidden(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status === 403;
}

function saveErrorKey(error: unknown): string {
  if (isForbidden(error)) return `${I18N}saveForbidden`;
  return SAVE_ERROR_BY_CODE[errorCode(error) ?? ''] ?? `${I18N}saveError`;
}

function assignErrorKey(error: unknown): string {
  if (isForbidden(error)) return `${I18N_ENTSCHEIDUNG}errors.assignForbidden`;
  return ASSIGN_ERROR_BY_CODE[errorCode(error) ?? ''] ?? `${I18N_ENTSCHEIDUNG}errors.assignFailed`;
}

function confirmErrorKey(error: unknown): string {
  if (isForbidden(error)) return `${I18N}confirmForbidden`;
  return `${I18N}${error instanceof HttpErrorResponse && error.status === 409 ? 'confirmNoDecision' : 'confirmError'}`;
}

function entwurfsStand(
  entscheid: ReleaseDecisionValue | null,
  kommentar: string,
  auflagen: readonly AuflageEntwurf[],
): string {
  const gefuellt = entscheid === 'AUFLAGEN' ? auflagen.filter((a) => a.text.trim()) : [];
  return JSON.stringify([entscheid, kommentar.trim(), gefuellt.map((a) => [a.text.trim(), a.wer])]);
}

const LEERER_STAND = entwurfsStand(null, '', []);

let nextId = 0;

@Component({
  selector: 'app-freigabe-entscheidung',
  standalone: true,
  imports: [TranslatePipe, DatePipe, NgTemplateOutlet, FormsModule, AvatarComponent, PersonSelectComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './freigabe-entscheidung.component.html',
  styleUrl: './freigabe-entscheidung.component.scss',
})
export class FreigabeEntscheidungComponent {
  private readonly releaseService = inject(ReleaseService);
  private readonly memberService = inject(ProjectMemberService);
  private readonly projectRoleService = inject(ProjectRoleService);

  readonly projectId = input.required<string>();
  readonly releaseId = input.required<string>();
  readonly assessment = input.required<ReleaseAssessment>();
  readonly teststufe = input<string | null>(null);

  readonly assessmentChange = output<ReleaseAssessment>();
  readonly protokollExportieren = output<void>();

  protected readonly decisions = DECISIONS;
  protected readonly commentMax = FIELD_LENGTHS.DECISION_COMMENT_MAX;
  protected readonly conditionMax = FIELD_LENGTHS.DECISION_CONDITION_MAX;
  protected readonly conditionsMax = FIELD_LENGTHS.DECISION_CONDITIONS_MAX;
  protected readonly anzahlRollen = ROLLEN.length;
  protected readonly idPrefix = `fe-${nextId++}`;

  protected readonly entscheid = signal<ReleaseDecisionValue | null>(null);
  protected readonly kommentar = signal('');
  protected readonly auflagen = signal<readonly AuflageEntwurf[]>([LEERE_AUFLAGE]);
  protected readonly versuch = signal(false);
  private readonly bearbeiten = signal(false);
  private readonly ausgangsStand = signal(LEERER_STAND);

  protected readonly saving = signal(false);
  protected readonly saveError = signal<string | null>(null);
  protected readonly confirming = signal(false);
  protected readonly confirmError = signal<string | null>(null);
  protected readonly assignError = signal<string | null>(null);
  /** Baut die Zeilen neu auf: So zeigt die Auswahl nach einer Ablehnung wieder die zugeordnete Person. */
  protected readonly auswahlStand = signal(0);
  protected readonly verlaufOffen = signal(false);

  protected readonly mitglieder = signal<PersonOption[]>([]);
  protected readonly mitgliederFehler = signal(false);
  protected readonly rollenOptionen = computed(() => this.mitglieder().filter((m) => m.kind !== 'AGENT'));

  private pendingAction = new Subscription();

  protected readonly isReadOnly = computed(
    () =>
      this.projectRoleService.isArchived() ||
      (this.projectRoleService.isProjectReader() && !this.projectRoleService.isTenantAdmin()),
  );
  protected readonly darfZuordnen = computed(
    () => this.projectRoleService.canManageProject() && !this.projectRoleService.isArchived(),
  );

  protected readonly empfehlung = computed(() => this.assessment().entscheidungsEmpfehlung);
  protected readonly aktuell = computed<ReleaseDecisionVersion | null>(
    () => this.assessment().entscheidungen[0] ?? null,
  );
  protected readonly fruehere = computed(() => this.assessment().entscheidungen.slice(1));

  readonly hasUnsavedChanges = computed(
    () => entwurfsStand(this.entscheid(), this.kommentar(), this.auflagen()) !== this.ausgangsStand(),
  );

  protected readonly formOffen = computed(() => this.bearbeiten() || !this.aktuell() || this.hasUnsavedChanges());

  protected readonly abweichend = computed(() => {
    const gewaehlt = this.entscheid();
    return !!gewaehlt && gewaehlt !== DECISION_FOR_RECOMMENDATION[this.empfehlung()];
  });
  protected readonly begruendungPflicht = computed(() => {
    const gewaehlt = this.entscheid();
    return this.abweichend() || gewaehlt === 'AUFLAGEN' || gewaehlt === 'NOGO';
  });
  private readonly gueltigeAuflagen = computed(() => this.auflagen().filter((a) => a.text.trim()));
  protected readonly begruendungFehlt = computed(() => this.begruendungPflicht() && !this.kommentar().trim());
  protected readonly auflageFehlt = computed(() => this.entscheid() === 'AUFLAGEN' && !this.gueltigeAuflagen().length);

  protected readonly rolleFehlt = computed(() => {
    const zuordnungen = this.assessment().zuordnungen;
    return ROLLEN.some((rolle) => {
      const zuordnung = zuordnungen.find((z) => z.rolle === rolle);
      return !zuordnung?.userId || zuordnung.aktiv === false;
    });
  });

  protected readonly anzahlBestaetigt = computed(
    () => this.aktuell()?.signoffs.filter((s) => s.bestaetigtAm).length ?? 0,
  );
  protected readonly vollstaendigBestaetigt = computed(() => this.anzahlBestaetigt() === ROLLEN.length);

  protected readonly folgeKey = computed(() => {
    if (!this.vollstaendigBestaetigt()) return `${I18N_ENTSCHEIDUNG}folge.pending`;
    return `${I18N_ENTSCHEIDUNG}folge.${this.aktuell()?.entscheidung === 'NOGO' ? 'nogo' : 'freigabe'}`;
  });

  protected readonly recommendationText = computed(() => {
    const { mussVerletzt, sollVerletzt } = this.assessment();
    if (mussVerletzt > 0) {
      return { key: `${I18N}recommendationText.${mussVerletzt === 1 ? 'mussOne' : 'muss'}`, count: mussVerletzt };
    }
    if (sollVerletzt > 0) {
      return { key: `${I18N}recommendationText.${sollVerletzt === 1 ? 'sollOne' : 'soll'}`, count: sollVerletzt };
    }
    return { key: `${I18N}recommendationText.alle`, count: 0 };
  });

  protected readonly signoffZeilen = computed<SignoffZeile[]>(() => {
    const version = this.formOffen() ? null : this.aktuell();
    const abnahmeOffen = !!this.aktuell() && !this.vollstaendigBestaetigt();
    return ROLLEN.map((rolle) => {
      const zuordnung = this.assessment().zuordnungen.find((z) => z.rolle === rolle) ?? null;
      const zeile = version?.signoffs.find((s) => s.rolle === rolle) ?? null;
      const quelle = version ? zeile : zuordnung;
      const bestaetigtAm = zeile?.bestaetigtAm ?? null;
      const fehlt = !bestaetigtAm && (!quelle?.userId || quelle.aktiv === false);
      return {
        rolle,
        name: quelle?.name ?? null,
        bestaetigtAm,
        fehlt,
        benachrichtigt: !!version && !bestaetigtAm && !fehlt,
        bestaetigbar: !!zeile?.eigene && !bestaetigtAm && !fehlt && !this.projectRoleService.isArchived(),
        auswahl: this.darfZuordnen() && !bestaetigtAm,
        leerErlaubt: !abnahmeOffen,
        inhaberId: zuordnung?.userId ?? null,
      };
    });
  });

  constructor() {
    inject(DestroyRef).onDestroy(() => this.pendingAction.unsubscribe());
    effect(() => {
      this.releaseId();
      untracked(() => this.fuerAnderesReleaseZuruecksetzen());
    });
    effect((onCleanup) => {
      const projectId = this.projectId();
      if (this.isReadOnly() && !this.darfZuordnen()) return;
      this.mitgliederFehler.set(false);
      const abruf = this.memberService.getMembers(projectId).subscribe({
        next: (members) => this.mitglieder.set(reviewerOptionsFromMembers(members)),
        error: () => this.mitgliederFehler.set(true),
      });
      onCleanup(() => abruf.unsubscribe());
    });
  }

  protected waehlen(wert: ReleaseDecisionValue): void {
    this.entscheid.set(wert);
    this.saveError.set(null);
  }

  protected auflageText(index: number, text: string): void {
    this.auflagen.update((liste) => liste.map((a, i) => (i === index ? { ...a, text } : a)));
  }

  protected auflageVerantwortlich(index: number, wer: string | null): void {
    this.auflagen.update((liste) => liste.map((a, i) => (i === index ? { ...a, wer } : a)));
  }

  protected auflageHinzufuegen(): void {
    this.auflagen.update((liste) => [...liste, LEERE_AUFLAGE]);
  }

  protected auflageEntfernen(index: number): void {
    this.auflagen.update((liste) => (liste.length > 1 ? liste.filter((_, i) => i !== index) : [LEERE_AUFLAGE]));
  }

  protected speichern(): void {
    const entscheidung = this.entscheid();
    this.versuch.set(true);
    if (!entscheidung || this.saving() || this.rolleFehlt() || this.begruendungFehlt() || this.auflageFehlt()) return;
    this.saving.set(true);
    this.saveError.set(null);
    this.pendingAction.add(
      this.releaseService
        .saveDecision(this.projectId(), this.releaseId(), {
          entscheidung,
          kommentar: this.kommentar().trim() || null,
          auflagen:
            entscheidung === 'AUFLAGEN'
              ? this.gueltigeAuflagen().map((a) => ({ text: a.text.trim(), verantwortlichId: a.wer }))
              : [],
        })
        .subscribe({
          next: (data) => {
            this.saving.set(false);
            this.entwurfVerwerfen();
            this.assessmentChange.emit(data);
          },
          error: (error: unknown) => {
            this.saving.set(false);
            this.saveError.set(saveErrorKey(error));
          },
        }),
    );
  }

  protected revidieren(): void {
    const version = this.aktuell();
    if (!version) return;
    const auflagen: AuflageEntwurf[] = version.auflagen.map((a) => ({ text: a.text, wer: a.verantwortlichId }));
    this.entscheid.set(version.entscheidung);
    this.kommentar.set('');
    this.auflagen.set(auflagen.length ? auflagen : [LEERE_AUFLAGE]);
    this.ausgangsStand.set(entwurfsStand(version.entscheidung, '', auflagen));
    this.versuch.set(false);
    this.bearbeiten.set(true);
    this.confirmError.set(null);
  }

  protected abbrechen(): void {
    this.entwurfVerwerfen();
  }

  protected bestaetigen(rolle: SignoffRole): void {
    this.confirming.set(true);
    this.confirmError.set(null);
    this.pendingAction.add(
      this.releaseService.confirmSignoff(this.projectId(), this.releaseId(), rolle, this.aktuell()?.version).subscribe({
        next: (data) => {
          this.confirming.set(false);
          this.assessmentChange.emit(data);
        },
        error: (error: unknown) => {
          if (errorCode(error) === 'SIGNOFF_NOT_LATEST') {
            this.confirmError.set(`${I18N}confirmStale`);
            this.nachVeralteterBestaetigungLaden();
          } else {
            this.confirming.set(false);
            this.confirmError.set(confirmErrorKey(error));
          }
        },
      }),
    );
  }

  protected zuordnen(zeile: SignoffZeile, userId: string | null): void {
    if ((userId || null) === zeile.inhaberId) return;
    this.assignError.set(null);
    this.pendingAction.add(
      this.releaseService.assignSignoff(this.projectId(), this.releaseId(), zeile.rolle, userId || null).subscribe({
        next: (data) => this.assessmentChange.emit(data),
        error: (error: unknown) => {
          this.assignError.set(assignErrorKey(error));
          this.auswahlStand.update((stand) => stand + 1);
        },
      }),
    );
  }

  private nachVeralteterBestaetigungLaden(): void {
    this.pendingAction.add(
      this.releaseService.getAssessment(this.projectId(), this.releaseId()).subscribe({
        next: (data) => {
          this.confirming.set(false);
          this.assessmentChange.emit(data);
        },
        error: () => this.confirming.set(false),
      }),
    );
  }

  private fuerAnderesReleaseZuruecksetzen(): void {
    this.pendingAction.unsubscribe();
    this.pendingAction = new Subscription();
    this.saving.set(false);
    this.confirming.set(false);
    this.confirmError.set(null);
    this.assignError.set(null);
    this.verlaufOffen.set(false);
    this.entwurfVerwerfen();
  }

  private entwurfVerwerfen(): void {
    this.entscheid.set(null);
    this.kommentar.set('');
    this.auflagen.set([LEERE_AUFLAGE]);
    this.ausgangsStand.set(LEERER_STAND);
    this.versuch.set(false);
    this.bearbeiten.set(false);
    this.saveError.set(null);
  }
}
